import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import StudentGapTask from 'Components/ChecklistTasks/StudentGapTask';
import StudentMathFormulasTask from 'Components/ChecklistTasks/StudentMathFormulasTask';
import StudentTestTask from 'Components/ChecklistTasks/StudentTestTask';
import StudentUnderlineTask from 'Components/ChecklistTasks/StudentUnderlineTask';
import StudentWordCompositionTask from 'Components/ChecklistTasks/StudentWordCompositionTask';
import StudentWordStressTask from 'Components/ChecklistTasks/StudentWordStressTask';

import { hwApi, taskApi } from '../../api';
import StudentExcludesTask from '../../Components/ChecklistTasks/StudentExludesTask';
import StudentSelectOneTask from '../../Components/ChecklistTasks/StudentSelectOneTask';
import StudentSentenceStructureTask from '../../Components/ChecklistTasks/StudentSentenceStructure';
import StudentTaskWithLink from '../../Components/ChecklistTasks/StudentTaskWithLink';
import StudentTextTask from '../../Components/ChecklistTasks/StudentTextTask';
import EditorProvider from '../../Components/ImageEditor/EditorProvider';
import { LoadingError } from '../../Components/LoadingError';
import { HW, TaskKeysType, TestItem } from '../../shared/types';
import { ExcludeAnswer, SelectOneAnswer, SummarizeTask } from '../../shared/types/task';

const componentMapping = {
  text: StudentTextTask,
  link: StudentTaskWithLink,
  test: StudentTestTask,
  exclude: StudentExcludesTask,
  selectOne: StudentSelectOneTask,
  sentenceStructure: StudentSentenceStructureTask,
  gap: StudentGapTask,
  mathFormulas: StudentMathFormulasTask,
  wordComposition: StudentWordCompositionTask,
  wordStress: StudentWordStressTask,
  underline: StudentUnderlineTask,
};

export default function HomeworkStudent() {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();
  const { state } = useLocation();

  const [hwData, setHwData] = useState<HW | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [tasks, setTasks] = useState<SummarizeTask[]>([]);

  useEffect(() => {
    const isAllTaskCompleted = tasks.every(it => Boolean(it.completedAt));

    if (hwData) {
      hwApi.updateHW({
        ...hwData,
        completed: isAllTaskCompleted,
      });
    }
  }, [tasks]);

  useEffect(() => {
    setLoading(true);

    hwApi.getHW(state.hwId).then(data => {
      setHwData(data);

      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const updateTasks = (tasks: SummarizeTask[]) => setTasks(tasks);
    const unsubscribe = taskApi.subscribeToICLTasks({ ICLId: state.hwId, setNewTasks: updateTasks });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleChangeTaskByKey = (
    id: string,
    key: TaskKeysType,
    value: string[] | string | boolean | ExcludeAnswer[] | TestItem[] | SelectOneAnswer[],
  ) => {
    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index], [key]: value };

    newTaskArray.splice(index, 1, updatedObject);

    if (updatedObject.completedAt || !updatedObject.completedAt) {
      taskApi.updateTask<SummarizeTask>(state.hwId, updatedObject);
    }

    setTasks(newTaskArray);
  };

  const saveTaskAnswer = async (id: string) => {
    setLoading(true);

    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index] };

    newTaskArray.splice(index, 1, updatedObject);

    await taskApi.updateTask<SummarizeTask>(state.hwId, updatedObject).then(() => {
      setLoading(false);
      toast.success(t('answer saved'));
    });
  };

  const renderTasks = () => {
    const activeTasks = tasks.filter(it => it.isActive);

    activeTasks.sort((a, b) => {
      if (a.indexNumber < b.indexNumber) {
        return -1;
      }

      return 0;
    });

    return activeTasks.map((it: SummarizeTask) => {
      const Component = componentMapping[it.type];

      return (
        <Component
          icl={hwData}
          key={it.id}
          {...it}
          handleChangeTaskByKey={handleChangeTaskByKey}
          saveTaskAnswer={saveTaskAnswer}
          hw
        />
      );
    });
  };

  return (
    <EditorProvider>
      <LoadingError isLoading={isLoading && !tasks}>
        <div className="mx-auto rounded-[24px] border-x-[8px] border-b-[8px] border-[#9CDFFC7F] border-opacity-50 bg-white px-3 py-4 shadow-none lg:w-11/12 lg:!px-[120px] lg:!py-[40px]">
          <div className="flex w-fit flex-col gap-[12px] lg:w-full lg:gap-[20px]">
            <div className="text-[24px] font-bold leading-9 lg:text-[48px] lg:leading-[55px]">
              {`${t('title')} ${t('hw')}: `}
              <span>{hwData?.title}</span>
            </div>

            <div className="flex flex-col gap-[12px] md:flex-row md:gap-[30%] lg:flex-row lg:gap-[30%]">
              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('subject')}:{' '}
                <span className="font-medium">
                  {' '}
                  {platformLang === 'ua' ? hwData?.subject.name : hwData?.subject.ruName}
                </span>
              </div>

              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('class')}: <span className="font-medium">{hwData?.class}</span>
              </div>
            </div>
          </div>

          <div>{renderTasks()}</div>
        </div>
      </LoadingError>
    </EditorProvider>
  );
}
